import { AuthProvider } from '@/contexts/AuthContext'
import { FeatureFlagsProvider } from '@/contexts/FeatureFlagsContext'
import { PluginProvider } from '@/contexts/PluginContext'
import { BusinessProvider } from '@/contexts/BusinessContext'
import { SystemModel } from '@/http/models/system/System'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import { useEffect } from 'react'

import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'

import { AppRoutes } from './AppRoutes'
import { store } from './clients/redux/store'
import { AppSettingsProvider } from './contexts/AppSettingsContext'
import { configService } from './services'
import { BusinessInactive } from './pages/system/BusinessInactive'
import { useBusinessInactive } from './lib/businessStatus'

const queryClient = new QueryClient()

const App = () => {
  const inactive = useBusinessInactive()
  const systemUid = localStorage.getItem('system_uid')
  const clientHost =
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      import.meta.env.VITE_ADMIN_URL
      ? import.meta.env.VITE_ADMIN_URL
      : window.location.hostname

  configService.setConfig({
    apiConfig: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      bearerToken: localStorage.getItem('access_token'),
      clientHost: clientHost,
      systemUid: systemUid,
    },
  })

  useEffect(() => {
    const fetchSystemInfo = async () => {
      try {
        const response = await SystemModel.getInfo()
        if (response.success && response.data) {
          const { system_uid } = response.data
          localStorage.setItem('system_uid', system_uid)
          const currentConfig = configService.getConfig()
          configService.setConfig({
            apiConfig: {
              ...currentConfig.apiConfig,
              systemUid: system_uid,
            },
          })
        }
      } catch (e) {
        console.error('Failed to fetch system info', e)
      }
    }
    if (!systemUid) {
      fetchSystemInfo()
    }
  }, [])

  if (inactive) return <BusinessInactive message={inactive} />

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <BusinessProvider>
            <Provider store={store}>
              <FeatureFlagsProvider>
                <AppSettingsProvider>
                  <PluginProvider>
                    <TooltipProvider>
                      <Sonner />
                      <AppRoutes />
                    </TooltipProvider>
                  </PluginProvider>
                </AppSettingsProvider>
              </FeatureFlagsProvider>
            </Provider>
          </BusinessProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
